"use client";

import { useState, useCallback, useDeferredValue, useMemo, useRef, memo } from "react";
import { Virtuoso } from "react-virtuoso";
import { VirtuosoErrorBoundary } from "@/components/virtuoso-error-boundary";
import { cn } from "@/lib/utils";
import type { DisplayPlayerProp } from "../utils-v2";
import { MobileOptimizerCard } from "./mobile-optimizer-card";
import { SmartEmptyState } from "../shared/smart-empty-state";
import type { TableVariant } from "../utils-v2";
import type { SportsbookId } from "@/lib/sportsbook-registry";
import type { Catalog, EventHeader } from "@/lib/odds-screen/live/types";
import type { HeldRows } from "@/hooks/use-order-hold";
import { oddsScreenHrefForLiveRow } from "@/lib/fantasy-props/odds-screen-link";
import type { OddsFormat } from "@/utils/calculators/odds-utils";
import type { BoardColumn } from "@/components/fantasy-props/model";
import {
  getOddsHistorySelectionIdentity,
  type OddsHistoryModalParams,
  type OpenOddsHistory,
} from "@/components/odds/odds-history-selection";
import { OddsHistoryModal } from "@/components/odds/lazy-odds-history-modal";

export type MobileCardsSlip = {
  readonly inSlip: (row: DisplayPlayerProp) => boolean;
  readonly toggle: (row: DisplayPlayerProp) => void;
};

interface MobileOptimizerCardsProps {
  slip?: MobileCardsSlip;
  showLeague?: boolean;
  rows: HeldRows<DisplayPlayerProp>;
  expanded: string | null;
  onToggle: (id: string, row: DisplayPlayerProp) => void;
  events?: ReadonlyMap<string, EventHeader>;
  tableVariant: TableVariant;
  platform: SportsbookId;
  columns: readonly BoardColumn[];
  platformOf?: (row: DisplayPlayerProp) => SportsbookId;
  catalog: Catalog | null;
  onShare: (row: DisplayPlayerProp) => void;
  onChatShare?: (row: DisplayPlayerProp) => void;
  onDismiss?: (row: DisplayPlayerProp) => void;
  className?: string;
  style?: React.CSSProperties;
  oddsFormat?: OddsFormat;
  footer?: React.ReactNode;
  onResetFilters?: () => void;
  onRetry?: () => void;
}

interface MobileCardsContext {
  footer?: React.ReactNode;
  expanded: string | null;
  oddsFormat: OddsFormat;
  displayInputsVersion: number;
  rows: HeldRows<DisplayPlayerProp>;
  events?: ReadonlyMap<string, EventHeader>;
}

function MobileCardsFooter({
  context,
}: {
  context?: MobileCardsContext;
}) {
  return <>{context?.footer ?? null}</>;
}

const VIRTUOSO_COMPONENTS = { Footer: MobileCardsFooter } as const;

const MOBILE_VIRTUOSO_STYLE = { height: "100%", width: "100%" } as const;
const MOBILE_VIRTUOSO_INCREASE_VIEWPORT_BY = { top: 320, bottom: 320 } as const;
const MOBILE_CARD_DEFAULT_HEIGHT = 287;
const computeMobileItemKey = (_index: number, id: string) => id;

export const MobileOptimizerCards = memo(function MobileOptimizerCards({
  expanded,
  onToggle,
  tableVariant,
  platform,
  columns,
  platformOf,
  catalog,
  onShare,
  onChatShare,
  onDismiss,
  slip,
  showLeague = false,
  rows,
  events,
  className,
  style,
  oddsFormat = "american",
  footer,
  onResetFilters,
  onRetry,
}: MobileOptimizerCardsProps) {
  const [historyModal, setHistoryModal] = useState<OddsHistoryModalParams | null>(null);
  const openHistory = useCallback<OpenOddsHistory>((params) => {
    setHistoryModal(params);
  }, []);

  const tableVariantRef = useRef(tableVariant);
  const platformRef = useRef(platform);
  const columnsRef = useRef(columns);
  const platformOfRef = useRef(platformOf);
  const catalogRef = useRef(catalog);
  const onShareRef = useRef(onShare);
  const onChatShareRef = useRef(onChatShare);
  const onDismissRef = useRef(onDismiss);
  const slipRef = useRef(slip);
  const showLeagueRef = useRef(showLeague);
  tableVariantRef.current = tableVariant;
  platformRef.current = platform;
  columnsRef.current = columns;
  platformOfRef.current = platformOf;
  catalogRef.current = catalog;
  onShareRef.current = onShare;
  onChatShareRef.current = onChatShare;
  onDismissRef.current = onDismiss;
  slipRef.current = slip;
  showLeagueRef.current = showLeague;

  const displayInputsVersionRef = useRef(0);
  const lastDisplayInputsRef = useRef<readonly unknown[]>([
    tableVariant,
    platform,
    columns,
    platformOf,
    catalog,
    onShare,
    onChatShare,
    onDismiss,
    slip,
    showLeague,
  ]);
  {
    const inputs = [tableVariant, platform, columns, platformOf, catalog, onShare, onChatShare, onDismiss, slip, showLeague] as const;
    if (inputs.some((value, i) => value !== lastDisplayInputsRef.current[i])) {
      lastDisplayInputsRef.current = inputs;
      displayInputsVersionRef.current += 1;
    }
  }
  const displayInputsVersion = displayInputsVersionRef.current;

  const virtuosoContext = useMemo<MobileCardsContext>(
    () => ({ footer, expanded, oddsFormat, displayInputsVersion, rows, events }),
    [footer, expanded, oddsFormat, displayInputsVersion, rows, events],
  );

  const deferredIds = useDeferredValue(rows.ids);

  const shareRow = useCallback((row: DisplayPlayerProp) => onShareRef.current(row), []);
  const chatShareRow = useCallback((row: DisplayPlayerProp) => onChatShareRef.current?.(row), []);
  const dismissRow = useCallback((row: DisplayPlayerProp) => onDismissRef.current?.(row), []);
  const toggleSlip = useCallback((row: DisplayPlayerProp) => slipRef.current?.toggle(row), []);

  const itemContent = useCallback(
    (index: number, id: string, context: MobileCardsContext) => {
      const row = context.rows.rowAt(id);
      if (row === null) return null;
      const isExpanded = context.expanded === id;
      const slipCurrent = slipRef.current;
      const inSlip = slipCurrent?.inSlip(row) ?? false;
      const event = row.event_id ? context.events?.get(row.event_id) : undefined;

      return (
        <article className="w-full px-3 py-1.5" role="listitem">
          <MobileOptimizerCard
            row={row}
            isExpanded={isExpanded}
            onToggle={onToggle}
            onShare={shareRow}
            onChatShare={onChatShareRef.current ? chatShareRow : undefined}
            onDismiss={onDismissRef.current ? dismissRow : undefined}
            slipState={slipCurrent ? (inSlip ? "in" : "out") : undefined}
            onSlipToggle={slipCurrent ? toggleSlip : undefined}
            leagueTag={showLeagueRef.current ? row.league ?? undefined : undefined}
            liveEvent={event?.live === undefined ? undefined : event}
            tableVariant={tableVariantRef.current}
            platform={platformOfRef.current?.(row) ?? platformRef.current}
            columns={columnsRef.current}
            oddsFormat={context?.oddsFormat ?? "american"}
            isFirstCard={index === 0}
            oddsScreenHref={oddsScreenHrefForLiveRow(catalogRef.current, row)}
            onOpenHistory={openHistory}
            lapsed={context.rows.lapsedProps(id)}
          />
        </article>
      );
    },
    [onToggle, shareRow, chatShareRow, dismissRow, toggleSlip, openHistory],
  );

  const virtuosoElement = useMemo(
    () => (
      <Virtuoso
        className="scrollbar-custom scrollbar-color-primary"
        {...rows.list}
        data={deferredIds}
        computeItemKey={computeMobileItemKey}
        increaseViewportBy={MOBILE_VIRTUOSO_INCREASE_VIEWPORT_BY}
        defaultItemHeight={MOBILE_CARD_DEFAULT_HEIGHT}
        style={MOBILE_VIRTUOSO_STYLE}
        context={virtuosoContext}
        components={VIRTUOSO_COMPONENTS}
        itemContent={itemContent}
      />
    ),
    [rows.list, deferredIds, virtuosoContext, itemContent],
  );

  if (rows.ids.length === 0) {
    return (
      <div className={cn("w-full flex flex-col", className)} style={{ height: "100%", ...style }}>
        <SmartEmptyState
          page={tableVariant === "props" ? "props" : "optimizer"}
          surface={tableVariant === "props" ? "fantasy-props" : "lines"}
          onResetFilters={onResetFilters}
          onRetry={onRetry}
        />
        {footer ? <div className="shrink-0">{footer}</div> : null}
      </div>
    );
  }

  return (
    <div className={cn("w-full flex flex-col", className)} style={{ height: "100%", ...style }}>
      <section className="flex-1 min-h-0" role="list">
        <VirtuosoErrorBoundary>{virtuosoElement}</VirtuosoErrorBoundary>
      </section>

      <OddsHistoryModal
        key={
          historyModal
            ? getOddsHistorySelectionIdentity(historyModal.selection)
            : "closed"
        }
        open={historyModal !== null}
        onOpenChange={(open) => {
          if (!open) setHistoryModal(null);
        }}
        selection={historyModal?.selection ?? null}
        fallbackSeries={historyModal?.fallbackSeries ?? null}
        peers={historyModal?.peers ?? null}
        initialView={historyModal?.initialView}
      />
    </div>
  );
});
